import { Check, Edit1 } from "@repo/icons";
import { CpsBadge } from "corpus";
import { useNavigate, useRouteContext } from "@tanstack/react-router";
import { getCurrentDate, ServicePackageEnum } from "@repo/lib";
import { addDays, format, parseISO } from "date-fns";
import { graphql } from "@repo/graphql-types/gql";
import { useFragment, type FragmentType } from "@repo/graphql-types";
import { CardRoot, CardContent, CardHeader } from "@/components/card";
import { trackEvent } from "@/lib/tracking";
import {
  IconListItem,
  IconListItemIcon,
  IconListItemText,
  IconListRoot,
} from "@/components/icon-list";
import { SubscribedAssistProPhoneNumberCard } from "@/components/subscribed-assist-pro/subscribed-assist-pro-phone-number-card";
import { SubscribedAssistProPrice } from "@/components/subscribed-assist-pro/subscribed-assist-pro-price";

export const SubscribedAssistFragment = graphql(/* GraphQL */ `
  fragment SubscribedAssistFragment on pacoteServicoPreco {
    ...SubscribedAssistProPriceFragment
  }
`);

interface SubscribedAssistProps {
  data: FragmentType<typeof SubscribedAssistFragment>;
}

export const SubscribedAssist = ({ data }: SubscribedAssistProps): JSX.Element => {
  const { user } = useRouteContext({ strict: false });
  const navigate = useNavigate();
  const proPackageService = user.pacotesServicos.find(
    (servicePkg) => servicePkg.codPacoteServicos === ServicePackageEnum.Pro.valueOf(),
  );

  const fragmentData = useFragment(SubscribedAssistFragment, data);

  const isTodayBeforeTrialEndDate =
    proPackageService?.dataFimTrial &&
    parseISO(proPackageService.dataFimTrial) >= getCurrentDate();

  const periodOfTrial = proPackageService?.dataFimTrial
    ? parseISO(proPackageService.dataFimTrial)
    : "";

  const periodOfTrialPlusOne = periodOfTrial !== "" ? addDays(periodOfTrial, 1) : "";

  const hasAlreadyCancelled = Boolean(proPackageService?.dataDesativacao);

  const onClickToCancelSubscription = (): void => {
    trackEvent("Botão Cancelar Assinatura Assist Pro Clicado");

    void navigate({
      to: "/settings/assist/unsubscribe-assist-pro/",
    });
  };

  const iconListItems: React.ReactElement[] = [];

  if (periodOfTrial !== "" && isTodayBeforeTrialEndDate) {
    iconListItems.push(
      <IconListItem key="trial-available">
        <IconListItemIcon>
          <Check />
        </IconListItemIcon>
        <IconListItemText>
          Período de teste gratuito até{" "}
          <strong>{format(periodOfTrial, "dd/MM/yyyy")}</strong>.
        </IconListItemText>
      </IconListItem>,
    );
  }

  if (periodOfTrialPlusOne !== "" && isTodayBeforeTrialEndDate) {
    iconListItems.push(
      <IconListItem key="trial-plus-one">
        <IconListItemIcon>
          <Check />
        </IconListItemIcon>
        <IconListItemText>
          O período de cobrança será iniciado no dia{" "}
          <strong>{format(periodOfTrialPlusOne, "dd/MM/yyyy")}</strong>.
        </IconListItemText>
      </IconListItem>,
    );
  }

  iconListItems.push(
    <IconListItem key="monthly-bill">
      <IconListItemIcon>
        <Check />
      </IconListItemIcon>
      <IconListItemText>
        A cobrança será incluída na sua fatura mensal da Livance.
      </IconListItemText>
    </IconListItem>,
    <IconListItem key="help-center">
      <IconListItemIcon>
        <Check />
      </IconListItemIcon>
      <IconListItemText>
        Confira nossa{" "}
        <a
          href="https://ajuda.livance.com.br/hc/pt-br/sections/41378634036755-Livance-Assist"
          target="_blank"
          rel="noopener"
          className="underline"
        >
          Central de Ajuda
        </a>{" "}
        para mais detalhes.
      </IconListItemText>
    </IconListItem>,
  );
  return (
    <>
      <div className="flex flex-col gap-2 pt-2 pb-8">
        <span className="text-2xl font-semibold">
          Confira aqui os detalhes do seu plano Livance Assist
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <SubscribedAssistProPhoneNumberCard />
        <CardRoot>
          <CardContent className="flex flex-col justify-center items-start w-full">
            <CardHeader className="py-0 px-0 flex flex-row justify-between items-center w-full">
              <CpsBadge>Mensal</CpsBadge>
              {!hasAlreadyCancelled ? (
                <button
                  type="button"
                  className="flex flex-row justify-center text-secondary-400 font-medium text-xs"
                  onClick={onClickToCancelSubscription}
                >
                  <Edit1 className="fill-secondary-400" />
                  Cancelar minha assinatura
                </button>
              ) : null}
            </CardHeader>
            <div className="pb-4">
              <SubscribedAssistProPrice data={fragmentData} />
            </div>
            <IconListRoot>{iconListItems}</IconListRoot>
          </CardContent>
        </CardRoot>
      </div>
    </>
  );
};
